import type { UnitData } from '../types/data';
import type { FactionState, ProvinceState } from '../types/game';
import { GameState } from '../game/GameState';
import { AdvisorManager } from './AdvisorManager';
import { DataRegistry } from './DataRegistry';

export interface CampaignModifiers {
  taxMultiplier: number;
  tradeMultiplier: number;
  recruitmentCostMultiplier: number;
  recruitmentMonthDelta: number;
  constructionMonthDelta: number;
  supplyCapacityBonus: number;
  roadSupplyBonus: number;
  portTradeBonus: number;
  foodOutputBonus: number;
  unrestMonthlyDelta: number;
  prosperityMonthlyDelta: number;
  espionageSuccessBonus: number;
  spyNetworkGrowthBonus: number;
  counterIntelligenceBonus: number;
  battleMoraleBonus: number;
  battleAttackMultiplier: number;
  advisorCapacityBonus: number;
  fortDefenseBonus: number;
  researchSpeedBonus: number;
  institutionSpreadBonus: number;
  missionRewardMultiplier: number;
  militaryTraditionGain: number;
  navalTraditionGain: number;
  diplomaticReputationBonus: number;
  catchUpBonus: number;
}

const baseModifiers: CampaignModifiers = {
  taxMultiplier: 1,
  tradeMultiplier: 1,
  recruitmentCostMultiplier: 1,
  recruitmentMonthDelta: 0,
  constructionMonthDelta: 0,
  supplyCapacityBonus: 0,
  roadSupplyBonus: 0,
  portTradeBonus: 0,
  foodOutputBonus: 0,
  unrestMonthlyDelta: 0,
  prosperityMonthlyDelta: 0,
  espionageSuccessBonus: 0,
  spyNetworkGrowthBonus: 0,
  counterIntelligenceBonus: 0,
  battleMoraleBonus: 0,
  battleAttackMultiplier: 1,
  advisorCapacityBonus: 0,
  fortDefenseBonus: 0,
  researchSpeedBonus: 0,
  institutionSpreadBonus: 0,
  missionRewardMultiplier: 1,
  militaryTraditionGain: 0,
  navalTraditionGain: 0,
  diplomaticReputationBonus: 0,
  catchUpBonus: 0,
};

const legacyTechnologyEffects: Record<string, Partial<CampaignModifiers>> = {
  wet_rice_administration: { taxMultiplier: 1.08, foodOutputBonus: 2, unrestMonthlyDelta: -1 },
  maritime_cartography: { tradeMultiplier: 1.12, portTradeBonus: 4, supplyCapacityBonus: 3 },
  gunpowder_drill: { battleAttackMultiplier: 1.12, battleMoraleBonus: 3, recruitmentMonthDelta: -1 },
  fortification_design: { fortDefenseBonus: 8, constructionMonthDelta: -1 },
  court_records: { taxMultiplier: 1.06, advisorCapacityBonus: 1, espionageSuccessBonus: 4 },
  supply_roads: { supplyCapacityBonus: 4, roadSupplyBonus: 3, recruitmentMonthDelta: -1 },
};

const lawEffects: Record<string, Partial<CampaignModifiers>> = {
  royal_council_act: { taxMultiplier: 1.03 },
  expanded_royal_council: { advisorCapacityBonus: 1, taxMultiplier: 1.04 },
  war_council_edict: { battleMoraleBonus: 5 },
  naval_colleges: { portTradeBonus: 10, tradeMultiplier: 1.05, navalTraditionGain: 0.08 },
  court_of_commerce: { tradeMultiplier: 1.1 },
  spy_directorate: { espionageSuccessBonus: 10, spyNetworkGrowthBonus: 0.08, counterIntelligenceBonus: 5 },
  council_of_clergy: { unrestMonthlyDelta: -2 },
  bureau_of_works: { constructionMonthDelta: -1 },
};

export class ModifierManager {
  static readonly instance = new ModifierManager();

  forFaction(factionId: string): CampaignModifiers {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const data = DataRegistry.instance.factions[factionId];
    const out = { ...baseModifiers };
    if (!faction) return out;

    out.taxMultiplier *= data?.economyModifiers?.tax ?? 1;
    out.tradeMultiplier *= data?.economyModifiers?.trade ?? 1;
    out.battleMoraleBonus += Math.round(((data?.militaryModifiers?.morale ?? 1) - 1) * 20);
    out.supplyCapacityBonus += Math.round(((data?.militaryModifiers?.supply ?? 1) - 1) * 10);

    for (const techId of faction.researchedTechs ?? []) this.apply(out, this.effectRecord(DataRegistry.instance.technologies[techId]?.effects) ?? legacyTechnologyEffects[techId]);
    for (const institutionId of faction.research?.adoptedInstitutions ?? []) this.apply(out, this.effectRecord(DataRegistry.instance.institutions[institutionId]?.effects));
    if (faction.research?.landDoctrineId) this.apply(out, this.effectRecord(DataRegistry.instance.doctrines[faction.research.landDoctrineId]?.effects));
    if (faction.research?.navalDoctrineId) this.apply(out, this.effectRecord(DataRegistry.instance.doctrines[faction.research.navalDoctrineId]?.effects));
    for (const lawId of faction.activeLaws ?? []) this.apply(out, lawEffects[lawId]);
    this.applyAdvisorEffects(out, faction);

    out.researchSpeedBonus += faction.research?.catchUpBonus ?? 0;
    out.counterIntelligenceBonus += Math.max(0, (faction.counterIntelligence ?? 20) - 20) * 0.08;
    out.diplomaticReputationBonus += faction.diplomaticReputation ?? 0;

    const exhaustionPenalty = Math.floor(faction.warExhaustion / 20);
    out.taxMultiplier = Math.max(0.55, out.taxMultiplier - exhaustionPenalty * 0.04);
    out.battleMoraleBonus -= exhaustionPenalty * 2;
    out.unrestMonthlyDelta += exhaustionPenalty;
    return out;
  }

  effectiveRecruitmentCost(factionId: string, unit: UnitData) {
    return Math.max(1, Math.ceil(unit.costGold * this.forFaction(factionId).recruitmentCostMultiplier));
  }

  effectiveRecruitmentMonths(factionId: string, unit: UnitData) {
    const base = unit.category.includes('cavalry') || unit.category.includes('artillery') ? 3 : 2;
    return Math.max(1, base + this.forFaction(factionId).recruitmentMonthDelta);
  }

  effectiveConstructionMonths(factionId: string, baseMonths: number) {
    return Math.max(1, baseMonths + this.forFaction(factionId).constructionMonthDelta);
  }

  provinceUnrestDelta(factionId: string, province: ProvinceState) {
    const campaign = GameState.instance.requireCampaign();
    const modifiers = this.forFaction(factionId);
    const factionReligion = campaign.factions[factionId]?.stateReligion ?? DataRegistry.instance.factions[factionId]?.stateReligion;
    if (factionReligion && province.religion && factionReligion !== province.religion) return modifiers.unrestMonthlyDelta + 1;
    return modifiers.unrestMonthlyDelta;
  }

  hasGunpowderEdge(factionId: string, unit: UnitData | undefined) {
    if (!unit) return false;
    const faction = GameState.instance.requireCampaign().factions[factionId];
    const label = `${unit.category} ${unit.name}`.toLowerCase();
    return Boolean(faction?.researchedTechs.includes('gunpowder_drill') && (label.includes('gun') || label.includes('musk') || label.includes('arquebus') || label.includes('cannon') || label.includes('artillery')));
  }

  private effectRecord(effects?: Record<string, number>) {
    if (!effects) return undefined;
    const partial: Partial<CampaignModifiers> = {};
    for (const [key, value] of Object.entries(effects)) {
      if (key in baseModifiers) partial[key as keyof CampaignModifiers] = value;
    }
    return partial;
  }

  private apply(out: CampaignModifiers, partial?: Partial<CampaignModifiers>) {
    if (!partial) return;
    for (const [key, value] of Object.entries(partial) as Array<[keyof CampaignModifiers, number]>) {
      if (key.endsWith('Multiplier')) out[key] *= value;
      else out[key] += value;
    }
  }

  private applyAdvisorEffects(out: CampaignModifiers, faction: FactionState) {
    for (const [role, advisorId] of Object.entries(faction.advisorSlots)) {
      if (!advisorId) continue;
      const advisor = AdvisorManager.instance.advisorsForFaction(faction.id).find((entry) => entry.id === advisorId);
      const skill = advisor?.skill ?? 4;
      if (role === 'Treasurer') out.taxMultiplier *= 1 + skill * 0.01;
      if (role === 'Marshal') { out.battleMoraleBonus += Math.ceil(skill / 2); out.militaryTraditionGain += skill * 0.01; }
      if (role === 'Chancellor') { out.tradeMultiplier *= 1 + skill * 0.008; out.diplomaticReputationBonus += Math.ceil(skill / 3); }
      if (role === 'High Priest') out.unrestMonthlyDelta -= Math.ceil(skill / 4);
      if (role === 'Spymaster') { out.espionageSuccessBonus += skill; out.spyNetworkGrowthBonus += skill * 0.008; out.counterIntelligenceBonus += Math.ceil(skill / 2); }
      if (role === 'Admiral') { out.portTradeBonus += Math.ceil(skill / 2); out.navalTraditionGain += skill * 0.01; }
      if (role === 'Architect') out.constructionMonthDelta -= skill >= 7 ? 1 : 0;
    }
  }
}